import { useEffect, useRef, useState } from "react";

// Cloud cover for the Conditions panel (freshwater-relevant: light penetration
// drives topwater/sight-fishing bites). Uses the same free Open-Meteo endpoint
// already used by useWind / useBarometricPressure — no API key.

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
const cloudCache = new Map();
const LS_KEY = "the99.cache.cloudCover";

function readPersisted() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persist(value) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(value)); } catch {}
}

function getCacheKey(lat, lng) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

function labelFor(percent) {
  if (percent <= 15) return "Clear";
  if (percent <= 50) return "Partly cloudy";
  if (percent <= 85) return "Mostly cloudy";
  return "Overcast";
}

function formatUpdatedAt(date) {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
}

export default function useCloudCover(location, { isOffline = false, enabled = true } = {}) {
  const [data, setData] = useState({
    label: "Loading…",
    status: "warn",
    percent: null,
    updatedAt: null,
    updatedLabel: null,
    error: null,
    loading: false,
  });
  const [refreshToggle, setRefreshToggle] = useState(0);
  const forceRefreshRef = useRef(false);

  const refresh = () => {
    forceRefreshRef.current = true;
    setRefreshToggle((c) => c + 1);
  };

  useEffect(() => {
    if (!enabled) {
      setData((prev) => ({ ...prev, loading: false }));
      return;
    }
    if (!location?.lat || !location?.lng) {
      setData({
        label: "GPS needed",
        status: "warn",
        percent: null,
        updatedAt: null,
        updatedLabel: null,
        error: "GPS needed",
        loading: false,
      });
      return;
    }

    const fetchCloud = async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const cacheKey = getCacheKey(location.lat, location.lng);
      const cached = cloudCache.get(cacheKey);

      setData((prev) => ({ ...prev, loading: true }));

      if (isOffline || !navigator.onLine) {
        const persisted = readPersisted();
        if (persisted) {
          setData({ ...persisted, loading: false });
        } else {
          setData({ label: "Offline", status: "warn", percent: null, updatedAt: null, updatedLabel: null, error: "Offline", loading: false });
        }
        return;
      }

      if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setData(cached.value);
        return;
      }

      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lng}&current=cloud_cover`
        );
        if (!response.ok) throw new Error("Cloud cover API request failed");

        const json = await response.json();
        const raw = json?.current?.cloud_cover;
        if (typeof raw !== "number") throw new Error("Cloud cover data unavailable");

        const percent = Math.round(raw);
        const updatedAt = new Date();
        const value = {
          label: `${labelFor(percent)} · ${percent}%`,
          status: "good",
          percent,
          updatedAt,
          updatedLabel: formatUpdatedAt(updatedAt),
          error: null,
          loading: false,
        };

        cloudCache.set(cacheKey, { value, timestamp: Date.now() });
        persist(value);
        setData(value);
      } catch (error) {
        console.error("Cloud cover fetch failed:", error);
        setData({
          label: "Unavailable",
          status: "warn",
          percent: null,
          updatedAt: null,
          updatedLabel: null,
          error: "Unavailable",
          loading: false,
        });
      }
    };

    fetchCloud();
  }, [location?.lat, location?.lng, refreshToggle, isOffline, enabled]);

  return { ...data, refresh };
}
